import { constants as config } from '@config';
import * as TextStylingService from 'src/services/text-styling';

import AbstractElement from '../abstractElement';

export default class LinkedTextList extends AbstractElement {
  allStyles;
  allContent;
  currentDesignId;
  chunkId;
  head;
  size;

  allPartialStyles;
  allParagraphStyles;
  allLetterSpacing;

  constructor () {
    super();

    this.size = 0;
    this.head = null;
    this.allStyles = [];
    this.currentDesignId = INKIVE.Tool._current_design_id;
    this.chunkId = 0;

    this.allContent = [];
    this.allPartialStyles = {};
    this.allParagraphStyles = {};
    this.allLetterSpacing = [];
  }

  addEnd (textarea) {
    const node = new INKIVE.Text.LinkedTextNode(textarea);
    let current;

    if (this.head == null) {
      this.head = node;
    } else {
      current = this.head;

      while (current.next) {
        current = current.next;
      }

      current.next = node;
    }

    this.size++;
  }

  calculateCurrentChunk () {
    this.chunkId = INKIVE.TextOverflowFactory.getCurrentChunkId(this.head.textarea);

    return this.chunkId;
  }

  render (canvas) {
    this.calculateCurrentChunk();

    this.calculateAllContentBeforeRender();
    this.calculateAllPartialStylesBeforeRender();
    this.calculateAllLetterSpacingBeforeRender();
    this.calculateAllParagraphStylesBeforeRender();

    let current = this.head;

    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      this.renderTextArea(canvas, current);

      if (!current.next) break;

      this.calculateRemainingContentAfterRender(current);
      this.calculateRemainingPartialStylesAfterRender();
      this.calculateRemainingLetterSpacingAfterRender();
      this.calculateRemainingParagraphStylesAfterRender();

      if (current.textarea.child_text.includes(this.currentDesignId)) {
        current = current.next;
      } else {
        INKIVE.TextOverflowFactory.setChunkContent(this.chunkId, this.allContent);
        INKIVE.TextOverflowFactory.setChunkPartialStyles(this.chunkId, this.allPartialStyles);
        INKIVE.TextOverflowFactory.setChunkParagraphStyles(this.chunkId, this.allParagraphStyles);
        INKIVE.TextOverflowFactory.setChunkLetterSpacing(this.chunkId, this.allLetterSpacing);

        break;
      }
    }
  }

  renderHiddenNormalTexts (canvas) {
    let current = this.head;

    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      current.textarea.render(canvas);
      current.textarea = new INKIVE.Text.Textarea().setDOM(current.textarea.dom, false, true);
      current = current.next;
      if (!current) break;
    }
  }

  renderHiddenTexts (canvas, isNormalTextRendering) {
    if (isNormalTextRendering) {
      this.renderHiddenNormalTexts(canvas);
      return;
    }

    this.calculateCurrentChunk();

    this.allContent = INKIVE.TextOverflowFactory.getChunkContent(this.chunkId);
    this.allPartialStyles = INKIVE.TextOverflowFactory.getChunkPartialStyles(this.chunkId);
    this.allParagraphStyles = INKIVE.TextOverflowFactory.getChunkParagraphStyles(this.chunkId);
    this.allLetterSpacing = INKIVE.TextOverflowFactory.getChunkLetterSpacing(this.chunkId);

    let current = this.head;

    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      this.renderTextArea(canvas, current);
      this.calculateRemainingContentAfterRender(current);
      this.calculateRemainingPartialStylesAfterRender();
      this.calculateRemainingLetterSpacingAfterRender();
      this.calculateRemainingParagraphStylesAfterRender();

      current = current.next;
      INKIVE.TextOverflowFactory.setChunkContent(this.chunkId, this.allContent[0] ? this.allContent : []);
      INKIVE.TextOverflowFactory.setChunkPartialStyles(this.chunkId, this.allPartialStyles);
      INKIVE.TextOverflowFactory.setChunkParagraphStyles(this.chunkId, this.allParagraphStyles);
      INKIVE.TextOverflowFactory.setChunkLetterSpacing(this.chunkId, this.allLetterSpacing);

      if (!current) break;
    }
  }

  renderTextArea (canvas, current) {
    const parentStyle = TextStylingService.mapTextToStyle(current.textarea.text);
    const narrowedStyles = TextStylingService.narrowStyles(this.allPartialStyles);
    const splittedStyles = TextStylingService.splitStyles(narrowedStyles);
    const cleanedStyles = TextStylingService.removeRedundantStyles(splittedStyles, parentStyle);

    current.textarea.text.setValues({
      textContent: this.allContent.join('\n'),
      shouldRecalculateStyle: false,
      isTextOverflowRendering: true,
      letter_spacing: this.allLetterSpacing,
      styles: cleanedStyles,
      ...this.allParagraphStyles
    });

    current.textarea.render(canvas);
    current.textarea = new INKIVE.Text.Textarea().setDOM(current.textarea.dom, false, true);
  }

  calculateAllContentBeforeRender () {
    let current = this.head;
    let content = '';

    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      const textToAppend = this.isLastLineEol(current.textarea.text.strings) && current.next
        ? current.textarea.text.textContent + '\n'
        : current.textarea.text.textContent;

      content += textToAppend;

      if (this.isProcessedHiddenText(current) || !current.next) break;
      current = current.next;
    }

    content += this.isProcessedHiddenText(current)
      ? INKIVE.TextOverflowFactory.getChunkContent(this.chunkId).join('\n')
      : '';

    this.allContent = content.split('\n');
  }

  calculateRemainingContentAfterRender (current) {
    const currentParagraphs = current.textarea.text.textContent.split('\n');
    const offsetLine = this.isLastLineEol(current.textarea.text.strings) ? 0 : 1;
    const lastPara = offsetLine ? currentParagraphs[currentParagraphs.length - 1] : '';

    this.allContent.splice(0, currentParagraphs.length - offsetLine);
    this.allContent[0] = this.allContent[0] ? this.allContent[0].slice(lastPara.length) : '';
  }

  isLastLineEol (strings) {
    if (!strings) return false;
    if (!strings.length) return false;

    return strings[strings.length - 1].isEol;
  }

  // Detect if the hidden text is already processed and stored in Chunk
  // isProcessedHiddenText is key function. Don't change it if you are not sure about it.
  isProcessedHiddenText (current) {
    return INKIVE.TextOverflowFactory.getChunkContent(this.chunkId)?.length &&
      !current.textarea.child_text?.includes(this.currentDesignId);
  }

  calculateAllPartialStylesBeforeRender () {
    let current = this.head;
    let tempContent = '';

    const { cursorPosition, newLetters, oldContent, textContent, styles } = current.textarea.text;
    const parentStyle = TextStylingService.mapTextToStyle(current.textarea.text);
    const realOldContentLength = TextStylingService.getRealContentLength(oldContent);
    const realNewContentLength = TextStylingService.getRealContentLength(textContent);

    tempContent += this.isLastLineEol(current.textarea.text.strings) && current.next
      ? current.textarea.text.textContent + '\n'
      : current.textarea.text.textContent;

    const numOfDifferenceRealChars = oldContent ? realNewContentLength - realOldContentLength : 0;
    if (cursorPosition !== null && numOfDifferenceRealChars !== 0) {
      const contentBeforeCursorPos = newLetters < 0
        ? oldContent.slice(0, cursorPosition)
        : oldContent.slice(0, cursorPosition - newLetters);

      const realContentLengthBeforeCursorPos = TextStylingService.getRealContentLength(contentBeforeCursorPos);
      const positionToShift = contentBeforeCursorPos.length - realContentLengthBeforeCursorPos;
      const realCursorPosition = cursorPosition - positionToShift;
      const spreadStyle = TextStylingService.spreadStyles(styles, oldContent, parentStyle);

      this.allPartialStyles = numOfDifferenceRealChars < 0
        ? TextStylingService.removeStyleFromPosition(spreadStyle, realCursorPosition, Math.abs(numOfDifferenceRealChars))
        : TextStylingService.addStyleToPosition(spreadStyle, realCursorPosition, Math.abs(numOfDifferenceRealChars));
    } else {
      this.allPartialStyles = TextStylingService.spreadStyles(styles, textContent, parentStyle);
    }

    this.allPartialStyles = TextStylingService.adjustSpreadStylesLength(this.allPartialStyles, TextStylingService.getRealContentLength(tempContent));

    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      if (this.isProcessedHiddenText(current) || !current.next) break;
      current = current.next;
      const currentStyles = current.textarea.text.styles;
      const currentContent = current.textarea.text.textContent;
      const currentParentStyle = TextStylingService.mapTextToStyle(current.textarea.text);
      const currentSpreadStyle = TextStylingService.spreadStyles(currentStyles, currentContent, currentParentStyle);

      tempContent += this.isLastLineEol(current.textarea.text.strings) && current.next
        ? current.textarea.text.textContent + '\n'
        : current.textarea.text.textContent;

      this.allPartialStyles = TextStylingService.concatSpreadStyles(this.allPartialStyles, currentSpreadStyle);
      this.allPartialStyles = TextStylingService.adjustSpreadStylesLength(this.allPartialStyles, TextStylingService.getRealContentLength(tempContent));
    }

    if (this.isProcessedHiddenText(current)) {
      this.allPartialStyles = TextStylingService.concatSpreadStyles(this.allPartialStyles, INKIVE.TextOverflowFactory.getChunkPartialStyles(this.chunkId));
    }
  }

  calculateRemainingPartialStylesAfterRender () {
    const fakeAllContent = this.allContent.join('\n');
    const realOldContentLength = TextStylingService.getRealContentLengthFromSpreadStyle(this.allPartialStyles);
    const lengthToRemove = realOldContentLength - TextStylingService.getRealContentLength(fakeAllContent);
    this.allPartialStyles = TextStylingService.spliceSpreadStyles(this.allPartialStyles, 0, lengthToRemove);
  }

  calculateAllLetterSpacingBeforeRender () {
    let current = this.head;
    let tempContent = '';
    const { cursorPosition, newLetters, oldContent, textContent } = current.textarea.text;

    tempContent += this.isLastLineEol(current.textarea.text.strings) && current.next
      ? current.textarea.text.textContent + '\n'
      : current.textarea.text.textContent;

    if (cursorPosition !== null && newLetters !== 0) {
      const oldLetterSpacing = TextStylingService.mapCommonStylingToArray(current.textarea.text.letter_spacing, oldContent);
      this.allLetterSpacing = TextStylingService.shiftLetterSpacing(oldContent, textContent, cursorPosition, newLetters, oldLetterSpacing);
    } else {
      const oldLetterSpacing = TextStylingService.mapCommonStylingToArray(current.textarea.text.letter_spacing, textContent);
      this.allLetterSpacing = oldLetterSpacing;
    }

    this.allLetterSpacing = TextStylingService.adjustLetterSpacingLength(this.allLetterSpacing, TextStylingService.getRealContentLength(tempContent));

    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      if (this.isProcessedHiddenText(current) || !current.next) break;
      current = current.next;
      const currentContent = current.textarea.text.textContent;
      const currentLetterSpacing = TextStylingService.mapCommonStylingToArray(current.textarea.text.letter_spacing, currentContent);

      tempContent += this.isLastLineEol(current.textarea.text.strings) && current.next
        ? current.textarea.text.textContent + '\n'
        : current.textarea.text.textContent;

      this.allLetterSpacing = this.allLetterSpacing.concat(currentLetterSpacing);
      this.allLetterSpacing = TextStylingService.adjustLetterSpacingLength(this.allLetterSpacing, TextStylingService.getRealContentLength(tempContent));
    }

    if (this.isProcessedHiddenText(current)) {
      this.allLetterSpacing = this.allLetterSpacing.concat(INKIVE.TextOverflowFactory.getChunkLetterSpacing(this.chunkId));
    }
  }

  calculateRemainingLetterSpacingAfterRender () {
    const fakeAllContent = this.allContent.join('\n');
    const realOldContentLength = this.allLetterSpacing.length;
    const lengthToRemove = realOldContentLength - TextStylingService.getRealContentLength(fakeAllContent);
    this.allLetterSpacing.splice(0, lengthToRemove);
  }

  calculateAllParagraphStylesBeforeRender () {
    let current = this.head;
    const { newLetters, oldContent, textContent } = current.textarea.text;
    const attributeMap = config.text.paragraphAttributesMap;

    this.allParagraphStyles = {};
    this.calculateTextAttributeArray(current.textarea.text);

    if (current.textarea.text.cursorPosition !== null && newLetters !== 0) {
      const difference = INKIVE.Tool.calculateStringContentDifference(oldContent, textContent);
      const cursorPosition = !Number.isNaN(current.textarea.text.cursorPosition) ? current.textarea.text.cursorPosition : difference.position + newLetters;
      const changes = INKIVE.Text.Helpers.getChangedLines(oldContent, textContent, cursorPosition, newLetters);
      const numOfParagraphs = newLetters !== 0 ? oldContent.split('\n').length : textContent.split('\n').length;

      const actionMap = {
        added: this.addAttribute,
        removed: this.removeAttribute
      };

      for (const attribute in attributeMap) {
        this.allParagraphStyles[attribute] = TextStylingService.mapCommonStylingToArray(current.textarea.text[attribute], null, numOfParagraphs);
        actionMap[changes.mode]?.(this.allParagraphStyles, attribute, changes, attributeMap[attribute]);
      }
    } else {
      for (const attribute in attributeMap) {
        this.allParagraphStyles[attribute] = TextStylingService.mapCommonStylingToArray(current.textarea.text[attribute], null, textContent.split('\n').length);
      }
    }

    for (let i = 0; i < config.text.maxTextOverflowLength; i++) {
      if (this.isProcessedHiddenText(current) || !current.next) break;
      const isLastLineEol = current.textarea.text.strings[current.textarea.text.strings.length - 1]?.isEol;
      current = current.next;
      const currentContent = current.textarea.text.textContent;
      const numOfCurrentParagraphs = currentContent.split('\n').length;
      const currentCommonStyling = {};

      this.calculateTextAttributeArray(current.textarea.text);

      for (const attribute in attributeMap) {
        currentCommonStyling[attribute] = TextStylingService.mapCommonStylingToArray(current.textarea.text[attribute], null, numOfCurrentParagraphs);
        if (!isLastLineEol) currentCommonStyling[attribute].shift();

        this.allParagraphStyles[attribute] = this.allParagraphStyles[attribute].concat(currentCommonStyling[attribute]);
      }
    }

    if (this.isProcessedHiddenText(current)) {
      for (const attribute in attributeMap) {
        const hiddenParagraphStyles = INKIVE.TextOverflowFactory.getChunkParagraphStyles(this.chunkId);
        const styleLength = this.allParagraphStyles[attribute].length + hiddenParagraphStyles[attribute].length;
        const contentLength = this.allContent.length;
        const toConcatStyle = styleLength > contentLength ? hiddenParagraphStyles[attribute].slice(styleLength - contentLength) : hiddenParagraphStyles[attribute];

        this.allParagraphStyles[attribute] = this.allParagraphStyles[attribute].concat(toConcatStyle);
      }
    }
  }

  calculateTextAttributeArray (text) {
    text.alignmentArray = [];
    text.triggeredArray = [];
    text.bulletArray = [];
    text.bulletStyleArray = [];
    text.orderedArray = [];
    text.orderedStyleArray = [];
    text.orderedNumberingStyleArray = [];
    text.orderedRegexArray = [];
    text.orderedStartArray = [];
    text.headingArray = [];

    for (let i = 0; i < text.strings.length; i++) {
      const string = text.strings[i];
      if (string.isEol || i === text.strings.length - 1) {
        text.alignmentArray.push(string.textAnchor);
        text.triggeredArray.push(string.isButtonTriggered);
        text.bulletArray.push(string.bulletLevel);
        text.bulletStyleArray.push(string.bulletStyle);
        text.orderedArray.push(string.orderedLevel);
        text.orderedStyleArray.push(string.orderedStyle);
        text.orderedNumberingStyleArray.push(string.orderedNumberingStyle);
        text.headingArray.push(string.heading);
      }

      if (i === 0 || (i > 0 && text.strings[i - 1].isEol)) {
        text.customBulletSymbolArray.push(string.customBulletSymbol);
        text.bulletFillArray.push(string.bulletFill);
        text.bulletFontSizeArray.push(string.bulletFontSize);
        text.bulletFontStyleArray.push(string.bulletFontStyle);
        text.bulletHighlightArray.push(string.bulletHighlight);
        text.bulletWidthArray.push(string.bulletWidth);
        text.bulletFontFamilyArray.push(string.bulletFontFamily);
        text.bulletCustomFontIdArray.push(string.bulletCustomFontId);

        text.orderedFillArray.push(string.orderedFill);
        text.orderedFontSizeArray.push(string.orderedFontSize);
        text.orderedWidthArray.push(string.orderedWidth);
        text.orderedFontFamilyArray.push(string.orderedFontFamily);
        text.orderedFontWeightArray.push(string.orderedFontWeight);
        text.orderedFontStyleArray.push(string.orderedFontStyle);
        text.orderedHighlightArray.push(string.orderedHighlight);
        text.orderedCustomFontIdArray.push(string.orderedCustomFontId);
        text.orderedRegexArray.push(string.orderedRegex);
        text.orderedStartArray.push(string.orderedStart);
      }
    }
  }

  handleCommonTextSpacing (difference) {
    const text = this.text;
    if (text.newLetters === 0) return;

    const attributeMap = config.text.paragraphAttributesMap;

    const actionMap = {
      added: this.addAttribute,
      removed: this.removeAttribute
    };

    const cursorPosition = typeof text.cursorPosition === 'number' ? text.cursorPosition : difference.position + text.newLetters;
    const changes = INKIVE.Text.Helpers.getChangedLines(text.oldContent, text.textContent, cursorPosition, text.newLetters);

    for (const attribute in attributeMap) {
      if (!Array.isArray(text[attribute])) continue;

      actionMap[changes.mode]?.(text, attribute, changes, attributeMap[attribute]);
    }
  }

  addAttribute (text, attribute, changes, defaultValue) {
    const previousLine = changes.lines[0] - 1;
    const newValue = attribute === 'orderedStartArray'
      ? defaultValue
      : text[attribute][previousLine] || defaultValue;

    for (let i = changes.lines.length - 1; i >= 0; i--) {
      text[attribute].splice(changes.lines[i], 0, newValue);
      if (attribute === 'paragraph_spacing' && previousLine >= 0) {
        text[attribute][previousLine] = Math.max(text[attribute][previousLine], 0);
      }
    }
  }

  removeAttribute (text, attribute, changes) {
    const indexToDelete = changes.lines[0] - 1 >= 0 && attribute === 'paragraph_spacing'
      ? changes.lines[0] - 1
      : changes.lines[0];
    text[attribute].splice(indexToDelete, changes.lines.length);
  }

  calculateRemainingParagraphStylesAfterRender () {
    const realOldContentLength = this.allContent.length;
    const lengthToRemove = Math.abs(realOldContentLength - TextStylingService.getRealContentLengthFromSpreadStyle(this.allParagraphStyles));
    for (const attribute in config.text.paragraphAttributesMap) {
      this.allParagraphStyles[attribute].splice(0, lengthToRemove);
    }
  }
}
